import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/PageUI";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Row = ({ label, value, id }) => <div className="settings-row"><span>{label}</span><b data-testid={`settings-${id}`}>{value || "—"}</b></div>;

const DeleteDialog = ({ open, setOpen }) => {
  const { logout } = useAuth(), navigate = useNavigate();
  const [password, setPassword] = useState(""), [confirm, setConfirm] = useState(""), [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/delete-account", { password, confirm }); toast.success("Your account has been deleted."); navigate("/", { replace: true }); setTimeout(logout, 0); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete your account."); setBusy(false); }
  };
  return <Dialog open={open} onOpenChange={setOpen}><DialogContent className="delete-dialog" data-testid="delete-account-dialog">
    <DialogHeader><DialogTitle>Delete your account?</DialogTitle><DialogDescription>This can't be undone.</DialogDescription></DialogHeader>
    <ul className="delete-list" data-testid="delete-account-consequences">
      <li>All your <b>Studio credits are lost</b> and are not refunded.</li>
      <li>Any active plan subscription is cancelled right away.</li>
      <li>Your campaigns, saved creators, clients, videos and uploads are permanently deleted.</li>
      <li>Payment records are kept only as required for bookkeeping.</li>
    </ul>
    <form onSubmit={submit} className="delete-form">
      <label>Your password<input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" data-testid="delete-account-password" /></label>
      <label><span>Type <b>DELETE</b> to confirm</span><input value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="delete-account-confirm" /></label>
      <div className="delete-actions"><button type="button" className="secondary-btn" onClick={() => setOpen(false)} data-testid="delete-account-cancel">Keep my account</button>
        <button className="danger-btn" disabled={busy || !password || confirm.trim().toUpperCase() !== "DELETE"} data-testid="delete-account-submit"><Trash2 size={15} />{busy ? "Deleting…" : "Delete account and lose credits"}</button></div>
    </form>
  </DialogContent></Dialog>;
};

export default function AccountSettings() {
  const { user } = useAuth(), [open, setOpen] = useState(false);
  return <><PageHeader eyebrow="Account" title="Account settings" description="Your profile details and account options." />
    <section className="settings-grid" data-testid="account-settings-page">
      <article className="panel"><span className="eyebrow">PROFILE</span><Row label="Name" value={user?.name} id="name" /><Row label="Email" value={user?.email} id="email" /><Row label="Company" value={user?.company} id="company" /><Row label="Plan" value={user?.plan} id="plan" /></article>
      <article className="panel danger-zone" data-testid="danger-zone"><span className="eyebrow">DANGER ZONE</span><h2><AlertTriangle size={18} /> Delete account</h2>
        <p>Permanently delete your account and workspace. Your Studio credits will be lost and any subscription is cancelled.</p>
        {user?.role === "admin" ? <p className="settings-note" data-testid="delete-account-admin-note">Administrator accounts can't be deleted from the app.</p>
          : <button className="danger-btn" onClick={() => setOpen(true)} data-testid="delete-account-button"><Trash2 size={15} />Delete my account</button>}
      </article>
    </section>
    <DeleteDialog open={open} setOpen={setOpen} /></>;
}
